/**
 * Hex Footprint Utilities (L3-MULTI-HEX)
 *
 * Functions for working with multi-hex unit footprints.
 * Supports custom footprint shapes assigned by Claude based on creature appearance.
 *
 * @module hex-math/footprint
 */
import { hexDistance } from './distance.js';
/**
 * Default footprint shapes (fallbacks if Claude doesn't provide custom shape).
 * Uses flat-top hex directions.
 */
export const DEFAULT_FOOTPRINT_SHAPES = {
    1: [],
    2: [{ q: 1, r: 0 }], // East (horizontal)
    3: [{ q: 1, r: 0 }, { q: 0, r: 1 }], // East + Southeast (L-shape)
    4: [{ q: 1, r: 0 }, { q: 0, r: -1 }, { q: 0, r: 1 }], // Cross: E + NW + SE (3 rows, 2 cols)
};
/**
 * Create a footprint from size and optional custom shape.
 *
 * @param size - Number of hexes (1, 2, or 3)
 * @param shape - Custom relative hex offsets (optional, uses default if not provided)
 * @returns HexFootprint object
 */
export function createFootprint(size, shape) {
    // Validate shape length matches size
    const relativeHexes = shape && shape.length === size - 1
        ? shape
        : (DEFAULT_FOOTPRINT_SHAPES[size] ?? []);
    return {
        size,
        relativeHexes,
    };
}
/**
 * Get all hexes occupied by a footprint at a given origin.
 *
 * @param origin - The origin hex position
 * @param footprint - The footprint defining relative hex offsets
 * @returns Array of all occupied hex coordinates
 */
export function getFootprintHexes(origin, footprint) {
    const hexes = [origin];
    for (const offset of footprint.relativeHexes) {
        hexes.push({
            q: origin.q + offset.q,
            r: origin.r + offset.r,
        });
    }
    return hexes;
}
/**
 * Get the visual center of a footprint for sprite positioning.
 * Returns the average position of all hexes in the footprint.
 *
 * @param origin - The origin hex position
 * @param footprint - The footprint defining relative hex offsets
 * @returns Center coordinate (may be fractional)
 */
export function getFootprintCenter(origin, footprint) {
    if (footprint.size === 1) {
        return origin;
    }
    const hexes = getFootprintHexes(origin, footprint);
    const avgQ = hexes.reduce((sum, h) => sum + h.q, 0) / hexes.length;
    const avgR = hexes.reduce((sum, h) => sum + h.r, 0) / hexes.length;
    return { q: avgQ, r: avgR };
}
/**
 * Check if all hexes in a footprint are within grid bounds.
 *
 * @param origin - The origin hex position
 * @param footprint - The footprint defining relative hex offsets
 * @param gridWidth - Grid width in hexes
 * @param gridHeight - Grid height in hexes
 * @returns True if all hexes are within bounds
 */
export function isFootprintInBounds(origin, footprint, gridWidth, gridHeight) {
    const hexes = getFootprintHexes(origin, footprint);
    return hexes.every(hex => hex.q >= 0 && hex.q < gridWidth &&
        hex.r >= 0 && hex.r < gridHeight);
}
/**
 * Validate that a footprint shape is connected (all hexes adjacent to at least one other).
 * This ensures valid footprint shapes where all hexes form a contiguous group.
 *
 * @param footprint - The footprint to validate
 * @returns True if the footprint is connected
 */
export function isFootprintConnected(footprint) {
    if (footprint.size === 1) {
        return true; // Single hex is trivially connected
    }
    // Build set of all hex positions relative to origin (0,0)
    const hexSet = new Set();
    hexSet.add('0,0'); // Origin
    for (const offset of footprint.relativeHexes) {
        hexSet.add(`${offset.q},${offset.r}`);
    }
    // BFS from origin to ensure all hexes are reachable
    const visited = new Set();
    const queue = [{ q: 0, r: 0 }];
    visited.add('0,0');
    // Flat-top hex neighbor directions
    const directions = [
        { q: 1, r: 0 }, // E
        { q: 0, r: 1 }, // SE
        { q: -1, r: 1 }, // SW
        { q: -1, r: 0 }, // W
        { q: 0, r: -1 }, // NW
        { q: 1, r: -1 }, // NE
    ];
    while (queue.length > 0) {
        const current = queue.shift();
        for (const dir of directions) {
            const neighbor = { q: current.q + dir.q, r: current.r + dir.r };
            const key = `${neighbor.q},${neighbor.r}`;
            if (hexSet.has(key) && !visited.has(key)) {
                visited.add(key);
                queue.push(neighbor);
            }
        }
    }
    // All hexes should be visited if connected
    return visited.size === hexSet.size;
}
/**
 * Calculate minimum distance between two units considering their footprints.
 * Returns the minimum hex distance between any hex of unit A and any hex of unit B.
 *
 * @param posA - Position of unit A (origin hex)
 * @param footprintA - Footprint of unit A (optional, defaults to single-hex)
 * @param posB - Position of unit B (origin hex)
 * @param footprintB - Footprint of unit B (optional, defaults to single-hex)
 * @returns Minimum hex distance between the two units
 */
export function footprintDistance(posA, footprintA, posB, footprintB) {
    const hexesA = footprintA
        ? getFootprintHexes(posA, footprintA)
        : [posA];
    const hexesB = footprintB
        ? getFootprintHexes(posB, footprintB)
        : [posB];
    let minDistance = Infinity;
    for (const a of hexesA) {
        for (const b of hexesB) {
            const dist = hexDistance(a, b);
            if (dist < minDistance) {
                minDistance = dist;
            }
        }
    }
    return minDistance;
}
/**
 * Find all hexes adjacent to a footprint (neighbors of any hex in the footprint,
 * but not part of the footprint itself).
 *
 * @param origin - The origin hex position
 * @param footprint - The footprint defining relative hex offsets
 * @param gridConfig - Optional grid config for boundary checking
 * @returns Array of adjacent hex coordinates
 */
export function getFootprintAdjacentHexes(origin, footprint, gridConfig) {
    const hexes = footprint
        ? getFootprintHexes(origin, footprint)
        : [origin];
    const footprintSet = new Set(hexes.map(h => `${Math.round(h.q)},${Math.round(h.r)}`));
    const adjacentSet = new Set();
    const result = [];
    // Flat-top hex neighbor directions
    const directions = [
        { q: 1, r: 0 }, // E
        { q: 0, r: 1 }, // SE
        { q: -1, r: 1 }, // SW
        { q: -1, r: 0 }, // W
        { q: 0, r: -1 }, // NW
        { q: 1, r: -1 }, // NE
    ];
    for (const hex of hexes) {
        for (const dir of directions) {
            const neighbor = {
                q: Math.round(hex.q) + dir.q,
                r: Math.round(hex.r) + dir.r,
            };
            const key = `${neighbor.q},${neighbor.r}`;
            // Not part of footprint and not already added
            if (!footprintSet.has(key) && !adjacentSet.has(key)) {
                // Check bounds if config provided
                if (gridConfig) {
                    if (neighbor.q < 0 || neighbor.q >= gridConfig.gridWidth ||
                        neighbor.r < 0 || neighbor.r >= gridConfig.gridHeight) {
                        continue;
                    }
                }
                adjacentSet.add(key);
                result.push(neighbor);
            }
        }
    }
    return result;
}
/**
 * Check if a footprint can occupy a position (no obstacles and within bounds).
 *
 * @param origin - The origin hex position
 * @param footprint - The footprint defining relative hex offsets
 * @param obstacles - Set of occupied hex keys ("q,r" format)
 * @param gridWidth - Grid width in hexes
 * @param gridHeight - Grid height in hexes
 * @returns True if footprint can occupy the position
 */
export function canFootprintOccupy(origin, footprint, obstacles, gridWidth, gridHeight) {
    const hexes = footprint
        ? getFootprintHexes(origin, footprint)
        : [origin];
    for (const hex of hexes) {
        const q = Math.round(hex.q);
        const r = Math.round(hex.r);
        // Check bounds
        if (q < 0 || q >= gridWidth || r < 0 || r >= gridHeight) {
            return false;
        }
        // Check obstacles
        const key = `${q},${r}`;
        if (obstacles.has(key)) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=footprint.js.map